import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import { useAlert } from '../Components/AlertContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { VoiceProfileApiProvider, useVoiceProfileApi } from '../Services/VoiceProfileApiProvider';
import { VoiceProfileCreateApi } from '../Services/VoiceProfileApiProviderTypes';
import VoiceProfileForm from './VoiceProfileForm';
import {
  VoiceProfileForm as FormState,
  INITIAL_FORM_STATE,
  mapFormToApi,
  validate,
} from './VoiceProfileHelpers';
function NewVoiceProfileContent() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { createVoiceProfile } = useVoiceProfileApi();
  const { getAgents, getAssistantOptions } = useAgentApi();
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<{ key: string; name: string }[]>([]);
  const [voices, setVoices] = useState<{ value: string; label: string }[]>([]);
  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [agentsData, options] = await Promise.all([
          getAgents(0, 1000),
          getAssistantOptions(),
        ]);
        setAgents(agentsData.items.map((agent) => ({ key: agent.key, name: agent.name })));
        setVoices(options.voices);
      } catch (error) {
        showAlert('fail', 'Failed to load options');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  const onCreate = async () => {
    if (!validate(form, showAlert)) return;
    try {
      setLoading(true);
      const formData: VoiceProfileCreateApi = mapFormToApi(form);
      const created = await createVoiceProfile(formData);
      showAlert('success', 'Voice profile was successfully created!');
      navigate(`/organizations/${organizationId}/voice-profiles/${created.key ?? ''}`, {
        state: { organizationId },
      });
    } catch (error) {
      showAlert('fail', 'Failed to create voice profile');
    } finally {
      setLoading(false);
    }
  };
  const onCancel = () => {
    navigate(`/organizations/${organizationId}/voice-profiles`);
  };
  if (loading) {
    return <PageLoader />;
  }
  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name || 'New Voice Profile'}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>
              <VoiceProfileForm form={form} agents={agents} voices={voices} onChange={setForm} />
            </div>
            <div className="buttons-container justify-end gap-12">
              <Button background="color2" size="medium" onClick={onCreate}>
                Create
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function NewVoiceProfile() {
  return (
    <AgentApiProvider>
      <VoiceProfileApiProvider>
        <NewVoiceProfileContent />
      </VoiceProfileApiProvider>
    </AgentApiProvider>
  );
}
export default NewVoiceProfile;
